<template>
  <el-dialog :visible.sync="visibleLocal" title="签署协议" width="80%" :close-on-click-modal="false"
    :close-on-press-escape="false" @close="onClose">
    <div v-loading="loading">
      <div class="toolbar">
        <el-button type="primary" size="mini" icon="el-icon-download" @click="downloadPdf"
          :disabled="!contractHtml || pdfLoading" :loading="pdfLoading">
          {{ pdfLoading ? '生成中...' : '下载PDF' }}
        </el-button>
        <el-button v-if="debugMode" type="info" size="mini" @click="toggleDebug">
          调试信息
        </el-button>
      </div>
      <div v-if="debugMode && debugInfo" class="debug-info">
        <pre>{{ debugInfo }}</pre>
      </div>
      <div class="contract-wrapper">
        <div id="contract-print" class="contract-print" v-html="contractHtml"></div>
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="onClose">关 闭</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getContractByDeclarationId } from '@/api/work/engineeringProject/merchants'
import html2Canvas from 'html2canvas'
import JsPDF from 'jspdf'

export default {
  name: 'ContractViewDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    declarationId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      visibleLocal: false,
      loading: false,
      contractHtml: '',
      pdfLoading: false,
      debugMode: false, // 新增调试模式开关
      debugInfo: '' // 新增调试信息
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.visibleLocal = val
        if (val && this.declarationId) {
          this.fetchContract()
        }
      }
    }
  },
  methods: {
    async fetchContract() {
      this.loading = true
      try {
        const res = await getContractByDeclarationId(this.declarationId)
        if (res.code === '200' || res.code === 200) {
          let data = res && res.data

          // 添加调试信息
          console.log('合同数据原始格式:', typeof data, data)

          if (typeof data === 'string') {
            try {
              data = JSON.parse(data)
              console.log('解析后的JSON数据:', data)
            } catch (e) {
              console.log('数据为纯HTML字符串，长度:', data.length)
              // data 为纯 HTML 字符串
            }
          }

          // 兼容多种返回结构：{ content: {...} } | { html: '...' } | '...html...' | {...直接内容}
          if (data && data.content) {
            console.log('使用content字段渲染')
            this.contractHtml = this.renderHtmlFromContent(data.content)
          } else if (data && data.html) {
            console.log('使用html字段渲染')
            this.contractHtml = data.html
          } else if (typeof data === 'string') {
            console.log('使用字符串数据渲染')
            this.contractHtml = data
          } else if (data && typeof data === 'object') {
            console.log('使用对象数据渲染')
            this.contractHtml = this.renderHtmlFromContent(data)
          } else {
            console.log('数据为空，显示默认提示')
            this.contractHtml = '<div style="padding:12px;color:#999;">暂无合同内容</div>'
          }

          console.log('最终渲染的HTML长度:', this.contractHtml.length)
        } else {
          this.$message.error(res.message || '获取合同失败')
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
        this.$message.error('获取合同失败')
      } finally {
        this.loading = false
      }
    },
    renderHtmlFromContent(content) {
      const meta = content.meta || {}
      const parties = content.parties || {}
      const pricing = content.pricing || {}
      const settlement = content.settlement || {}
      const materials = content.materials || {}
      const dates = content.dates || {}
      const signatures = content.signatures || {}

      const SEAL_URL = 'https://joinstech-public.oss-cn-shenzhen.aliyuncs.com/f0cd715481345029fe8b21ed84612a501757062166333'

      const fmt = (v) => (v == null || v === '' ? '--' : v)
      const signImg = (url) => {
        if (!url) return '<span style="color:#bbb">未签名</span>'
        return `<img src="${url}" style="height:80px;max-width:100%;object-fit:contain;" />`
      }
      // const join = (arr) => (Array.isArray(arr) ? arr.join('、') : fmt(arr))

      // 与小程序协议条款保持一致
      const materialLabels = {
        pert_red: '日丰PERT樱桃红家装地暖管(樱桃红)',
        pert_blue: '日丰PERT三层阻氧地暖管(蓝色)',
        pert_yellow: '日丰PERT清洁黄家装地暖管(黄色)',
        pexb_red: '日丰PEXB三层阻氧地暖管(红色)',
        pert_5layer: '日丰PERT五层阻氧家装地暖管(红色)',
        pb_white: '日丰PB家装地暖管(象牙白)',
        pert_clear: '日丰PERT亮光地暖管(无色)',
        pert_gold: '日丰PERT精品地暖管(金色)',
        pert_brown: '日丰PERT高抗垢地暖管(红棕色)',
        pert_green: '日丰PERT翡翠绿地暖管(翡翠绿)',
        manual_sep: '日丰手动分水器',
        auto_sep: '日丰自动分水器',
        valve: '日丰供回水球阀',
        main_pipe: '日丰主管道',
        controller: '日丰温控器',
        heat_source: '热源设备及型号',
        protection_layer: '保护层施工',
        other: '其他',
        accessories: '日丰系列配套材料(挤塑板、反射膜、塑料管卡(红色)、保温护套管(红蓝)、护套弯管(红色))'
      }
      // 与小程序一致的材料摘要（逐项、带序号）
      const selected = Array.isArray(materials.selected) ? materials.selected : []
      const summaryParts = []
      selected.forEach(name => {
        if (name === 'accessories') {
          summaryParts.push(materialLabels.accessories)
        } else if (name === 'heat_source' && materials.heatSourceDesc) {
          summaryParts.push(`${materialLabels.heat_source}：${materials.heatSourceDesc}`)
        } else if (name === 'protection_layer' && materials.protectionLayerDesc) {
          summaryParts.push(`${materialLabels.protection_layer}：${materials.protectionLayerDesc}`)
        } else if (name === 'other' && materials.otherMaterials) {
          summaryParts.push(`${materialLabels.other}：${materials.otherMaterials}`)
        } else {
          summaryParts.push(materialLabels[name] || name)
        }
      })
      if (!selected.includes('accessories')) {
        summaryParts.push(materialLabels.accessories)
      }
      const materialsHtml = summaryParts.map((t, i) => `<div style="margin:8px 0;padding-left:2em;text-align:justify;position:relative;line-height:1.8;">${i + 1}、 ${t}</div>`).join('')

      // 生成合同签订日期
      const contractDate = dates.contract ? 
        `${fmt(dates.contract.year)}年${fmt(dates.contract.month)}月${fmt(dates.contract.day)}日` : 
        '--年--月--日'

      return `
        <div style="width:210mm;min-height:297mm;margin:0 auto;padding:10mm 20mm;background:#fff; rgba(0,0,0,0.1);box-sizing:border-box;position:relative;">
          <!-- 合同标题区域 -->
          <h2 style="text-align:center;font-size:26px;font-weight:bold;margin-bottom:10px;letter-spacing:3px;color:#000;padding:10px 0;border-bottom:2px solid #000;border-top:2px solid #000;margin-top:20px;">日丰地暖质保协议</h2>
          <div style="text-align:center;font-size:16px;color:#444;margin-bottom:20px;letter-spacing:2px;font-weight:500;">安装(家装)施工合同</div>
          
          <!-- 装饰线 -->
          <div style="width:100%;height:2px;background:linear-gradient(90deg,transparent,#333,transparent);margin:15px 0;"></div>
          
          <!-- 合同基本信息 -->
          <div style="text-align:right;margin:10px 0;font-size:14px;color:#333;"><span style="font-weight:bold;">合同编号(${fmt(dates.contract && dates.contract.year)})：</span>${fmt(meta.contractNumber)}</div>
          <div style="text-align:right;margin:10px 0;font-size:14px;color:#333;"><span style="font-weight:bold;">签订日期：</span>${contractDate}</div>
          <div style="text-align:right;margin:10px 0;font-size:14px;color:#333;"><span style="font-weight:bold;">商家编号：</span>${fmt(meta.merchantNumber)}</div>
          
          <!-- 引言 -->
          <div style="font-style:italic;text-align:justify;padding:15px;margin:20px 0;background-color:#f9f9f9;border-left:4px solid #ddd;line-height:2;">根据《民法典》有关规定，和行业相关标准，结合本工程的具体情况，经双方协商一致，同意签订本合同，并遵守、严格执行。</div>
          
          <!-- 合同各方信息 -->
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">第一联(白)安装单位   第二联(粉)安装用户   第三联(蓝)鉴证方</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">需方(甲方)：${fmt(parties.jiafang && parties.jiafang.name)}</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">供方(乙方)：${fmt(parties.yifang && parties.yifang.name)}</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">鉴证方(丙方、厂家、厂家授权代理商)：${fmt(parties.bingfang && parties.bingfang.name)}</div>
          
          <!-- 装饰线 -->
          <div style="width:100%;height:2px;background:linear-gradient(90deg,transparent,#333,transparent);margin:15px 0;"></div>

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">一、合同关系说明</h3>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">乙方为丙方授权合作单位，丙方为隶属于日丰管企业股份有限公司的自律性组织，乙方不得以丙方的名义从事非法、违法、无诚信行为，否则自负其责。在乙方为甲方提供的施工工程服务中，丙方承担日丰地暖品牌管材、管件及相关配件的质量责任。</div>

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">二、安装内容及选材</h3>
          <div style="page-break-inside:avoid;">${materialsHtml}</div>
          <div style="color:red;text-align:justify;text-indent:2em;line-height:2;font-size:15px;"><strong>*其他材料：</strong>${fmt(materials.otherMaterials)}</div>

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">三、安装造价</h3>
          <table style="width:100%;border:1px solid #000;border-collapse:collapse;margin:20px 0;font-size:14px;">
            <tr><td style="border:1px solid #000;padding:10px 12px;text-align:center;">地暖单价</td><td style="border:1px solid #000;padding:10px 12px;text-align:center;">${fmt(pricing.unitPrice)} 元/㎡</td></tr>
            <tr><td style="border:1px solid #000;padding:10px 12px;text-align:center;">安装面积</td><td style="border:1px solid #000;padding:10px 12px;text-align:center;">${fmt(pricing.area)} ㎡</td></tr>
            <tr><td style="border:1px solid #000;padding:10px 12px;text-align:center;">水泵中心</td><td style="border:1px solid #000;padding:10px 12px;text-align:center;">${fmt(pricing.pumpCenter)} 元</td></tr>
            <tr><td style="border:1px solid #000;padding:10px 12px;text-align:center;">热源设备</td><td style="border:1px solid #000;padding:10px 12px;text-align:center;">${fmt(pricing.heatSource)} 元</td></tr>
            <tr><td style="border:1px solid #000;padding:10px 12px;text-align:center;">地暖保护层</td><td style="border:1px solid #000;padding:10px 12px;text-align:center;">${fmt(pricing.protectionLayer)} 元/㎡</td></tr>
            <tr><td style="border:1px solid #000;padding:10px 12px;text-align:center;">其他</td><td style="border:1px solid #000;padding:10px 12px;text-align:center;">${fmt(pricing.otherCost)} 元</td></tr>
          </table>
          <div style="text-align:right;margin:20px 0;font-size:18px;font-weight:bold;color:#000;">总造价：<strong>${fmt(pricing.totalPrice)}</strong> 元</div>
          ${pricing.totalPriceChinese ? `<div style="text-align:right;margin:10px 0;font-size:14px;font-weight:bold;color:#000;font-family:'SimSun','宋体',serif;">大写：${fmt(pricing.totalPriceChinese)}</div>` : ''}

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">四、结算方式及期限</h3>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">合同签订以后，甲方按乙方要求首付(${fmt(settlement.deposit)})地暖定金，乙方到甲方现场测量房屋，经甲方签字确认后，货到现场收取90％的地暖总造价，余款在盘管结束，日丰检测试压验收合格后付清。</div>
          ${settlement.depositChinese ? `<div style="background-color:#fff3cd;padding:10px 15px;border-left:4px solid #ffc107;margin:15px 0;font-weight:bold;">定金大写：${fmt(settlement.depositChinese)}</div>` : ''}

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">五、施工条件要求</h3>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">1、现场地面清理干净、找平地面。</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">2、厨房及卫生间冷热水管路安装完毕且打压试验完毕。</div>

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">六、施工质量及验收标准</h3>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">1、地暖工程安装依照《低温热水地板辐射采暖系统施工安装》(03K404)及相关标准验收。</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">2、系统安装完毕进行压力试验，水压试验压力应为工作压力的1.5 倍，且不应小于0.6MPa，在试验压力下，稳压 1h,其压力降不应大于0.05MPa,且不渗不漏为合格。</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">3、客户领取日丰安全卫士预约质保卡，进行预约试压，建立用户验收施工档案，保存施工图片资料，制作VR实景视频存档。</div>

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">七、厂家承诺</h3>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">1、日丰家装专用地暖管道的使用寿命是50年，除人为因素外，由于管材质量问题出现相关的损失均由厂家负责。</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">2、日丰地暖分水器、过滤器、活接式球阀、双活接球阀质保为5年，温控器和执行器质保为3年。</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">3、如乙方在保修期内不履行维修义务，丙方将强制要求乙方进行售后服务，保证甲方权益。</div>

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">八、乙方承诺</h3>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">1、采暖系统安装并验收合格后，乙方将提供贰个采暖期的免费保修期，保修期后，乙方将提供有偿维修服务。(冬季排水有偿服务，调试为免费服务。)</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">2、在保修期内所有设备、材料因其安装质量而出现问题，均由乙方负责无偿维修，并承担因此造成的损失。</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">3、乙方在接到甲方的维修要求后，第一时间内必须到现场，进行事故处理和分析事故原因及责任分配原则处理。由甲方人为因素出现的任何问题，责任均由甲方承担。</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">4、保修期后乙方按成本价格收取维修费用。</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">5、乙方铺设地暖盘管道每组地暖管道必须是整管，中间不能有接头出现，一经发现甲方有权让乙方整改，并向厂家进行投诉。</div>

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">九、其他事项</h3>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">1、禁止在地暖采暖结构层上凿洞、打钉、钻孔或与热源体直接接触，违者如出现故障由甲方承担后果。</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">2、开始供暖给水时室内必须留人。</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">3、地面宜采用地砖或地暖专用复合地板，铺装木地板作为地面饰材必须采用地暖专用防潮垫。</div>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">4、其它：${fmt(content.terms && content.terms.otherItems)}</div>

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">十、违约责任</h3>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">按《合同法》有关规定执行。</div>

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">十一、争议解决</h3>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">合同执行过程中如果发生争议，双方应及时协商解决，协商不成时可向当地人民法院申请裁决。</div>

          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">十二、本合同一式叁份</h3>
          <div style="color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:15px;">甲、乙、丙方各执一份。</div>
          
          <!-- 装饰线 -->
          <div style="width:100%;height:2px;background:linear-gradient(90deg,transparent,#333,transparent);margin:1px 0;"></div>

          <!-- 签名区域 -->
          <h3 style="margin:0 20px;font-size:18px;font-weight:bold;color:#000;padding-left:15px;border-left:4px solid #000;page-break-after:avoid;text-align:left;letter-spacing:1px;">签名区</h3>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:15px;margin-top:40px;position:relative;width:100%;box-sizing:border-box;justify-items:stretch;align-items:stretch;">
            <div style="min-height:180px;display:flex;flex-direction:column;position:relative;background-color:#fff;box-sizing:border-box;">
              <div style="font-weight:bold;margin-bottom:15px;display:block;text-align:center;font-size:16px;color:#000;">需方(甲方)：</div>
              <div style="flex:1;display:flex;align-items:center;justify-content:center;position:relative;min-height:80px;">${signImg(signatures.jiafang)}</div>
              <div style="margin:2px 0;color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:12px;display:flex;">委托代理人：${fmt(parties.jiafang && parties.jiafang.agent)}</div>
              <div style="margin:2px 0;color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:12px;display:flex;">地址：${fmt(parties.jiafang && parties.jiafang.address)}</div>
              <div style="margin:2px 0;color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:12px;display:flex;">电话：${fmt(parties.jiafang && parties.jiafang.phone)}</div>
            </div>

            <div style="min-height:180px;display:flex;flex-direction:column;position:relative;background-color:#fff;box-sizing:border-box;">
              <div style="font-weight:bold;margin-bottom:15px;display:block;text-align:center;font-size:16px;color:#000;">供方(乙方)：</div>
              <div style="flex:1;display:flex;align-items:center;justify-content:center;position:relative;min-height:80px;">${signImg(signatures.yifang)}</div>
              <div style="margin:2px 0;color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:12px;display:flex;">委托代理人：${fmt(parties.yifang && parties.yifang.agent)}</div>
              <div style="margin:2px 0;color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:12px;display:flex;">地址：${fmt(parties.yifang && parties.yifang.address)}</div>
              <div style="margin:2px 0;color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:12px;display:flex;">电话：${fmt(parties.yifang && parties.yifang.phone)}</div>
            </div>

            <div style="min-height:180px;display:flex;flex-direction:column;position:relative;background-color:#fff;box-sizing:border-box;">
              <div style="font-weight:bold;margin-bottom:15px;display:block;text-align:center;font-size:16px;color:#000;">鉴证方(丙方)：</div>
              <div style="flex:1;display:flex;align-items:center;justify-content:center;position:relative;min-height:80px;">
                <div style="color:#666;font-style:italic;">丙方公章</div>
                <img src="${SEAL_URL}" style="height:100px;width:100px;opacity:0.85;mix-blend-mode:multiply;filter:contrast(1.3) brightness(0.9) saturate(1.2);position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:10;" />
              </div>
              <div style="margin:2px 0;color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:12px;display:flex;">地址：${fmt(parties.bingfang && parties.bingfang.address)}</div>
              <div style="margin:2px 0;color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:12px;display:flex;">电话：${fmt(parties.bingfang && parties.bingfang.phone)}</div>
              <div style="margin:2px 0;color:#333;text-align:justify;text-indent:2em;line-height:2;font-size:12px;display:flex;">时间：${fmt(dates.bingfang && dates.bingfang.year)}年${fmt(dates.bingfang && dates.bingfang.month)}月${fmt(dates.bingfang && dates.bingfang.day)}日</div>
            </div>
          </div>
        </div>
      `
    },
    async downloadPdf() {
      // 防止重复点击
      if (this.pdfLoading) return

      this.pdfLoading = true
      this.$message.info('正在生成PDF，请稍候...')

      // 更新调试信息
      if (this.debugMode) {
        this.debugInfo = '开始PDF生成过程...\n';
      }

      try {
        // 确保DOM内容完全渲染后再生成PDF
        await this.$nextTick();
        // 等待更长时间确保v-html内容完全渲染和图片加载
        await new Promise(resolve => setTimeout(resolve, 2000));

        if (this.debugMode) {
          this.debugInfo += 'DOM渲染完成，等待图片加载...\n';
        }

        const contractElement = document.querySelector('#contract-print')
        if (!contractElement) {
          this.$message.error('合同内容元素未找到')
          return
        }

        // 检查内容是否为空
        if (!contractElement.innerHTML || contractElement.innerHTML.trim() === '') {
          this.$message.error('合同内容为空，请稍后重试')
          return
        }

        // 等待所有图片加载完成
        if (this.debugMode) {
          this.debugInfo += '开始等待图片加载...\n';
        }
        await this.waitForImagesLoaded(contractElement)

        if (this.debugMode) {
          this.debugInfo += '图片加载完成，开始转换图片...\n';
        }

        // 处理所有图片，转换为DataURL
        await this.convertImagesToDataURL(contractElement)

        // 再次等待确保图片转换完成
        await new Promise(resolve => setTimeout(resolve, 2000));

        // 验证图片是否已正确转换
        const images = contractElement.querySelectorAll('img');
        let allImagesConverted = true;
        let convertedCount = 0;
        images.forEach(img => {
          if (img.src && !img.src.startsWith('data:')) {
            allImagesConverted = false;
            console.warn('图片未转换:', img.src);
          } else if (img.src && img.src.startsWith('data:')) {
            convertedCount++;
          }
        });

        if (this.debugMode) {
          this.debugInfo += `图片转换完成: ${convertedCount}/${images.length} 张图片已转换\n`;
          if (!allImagesConverted) {
            this.debugInfo += '警告: 部分图片未转换，继续尝试生成PDF\n';
          }
        }

        if (!allImagesConverted) {
          console.warn('部分图片未转换，继续尝试生成PDF');
        }

        if (this.debugMode) {
          this.debugInfo += '开始生成PDF页面...\n';
        }

        // 改进的PDF生成策略，确保内容完整打印
        // 创建PDF文档 - A4大小
        const pdf = new JsPDF('p', 'mm', 'a4')

        // PDF页面参数
        const a4Width = 210 // A4宽度（mm）
        const a4Height = 297 // A4高度（mm）
        const margin = 10 // 统一页边距（mm），确保左右边距相同
        const contentWidth = a4Width - (margin * 2) // 内容区域宽度（mm）
        const contentHeight = a4Height - (margin * 2) // 内容区域高度（mm）

        // 创建主临时容器，优化样式设置以确保左右边距一致
        const tempParent = document.createElement('div')
        tempParent.style.position = 'absolute'
        tempParent.style.top = '-9999px'
        tempParent.style.left = '-9999px'
        tempParent.style.width = `${contentWidth * 3.78}px` // 转换mm为像素（1mm ≈ 3.78px）
        tempParent.style.backgroundColor = '#ffffff'
        tempParent.style.padding = '0'
        tempParent.style.margin = '0 auto' // 确保水平居中
        tempParent.style.boxSizing = 'border-box'
        tempParent.style.textAlign = 'left' // 确保文本对齐一致
        document.body.appendChild(tempParent)

        // 克隆原始内容到临时容器，并保留所有样式
        const tempContent = contractElement.cloneNode(true)
        tempContent.style.width = '100%'
        tempContent.style.margin = '0'
        tempContent.style.padding = '0'
        tempContent.style.boxSizing = 'border-box'
        tempContent.style.fontSize = '14px'
        tempContent.style.lineHeight = '1.6'
        tempContent.style.color = '#333'

        // 优化丙方公章显示效果
        this.optimizeSealDisplay(tempContent);

        // 为签名区域添加防分页样式
        this.addSignatureNoPageBreak(tempContent);

        tempParent.appendChild(tempContent)

        // 强制回流，确保准确计算尺寸
        tempParent.offsetHeight

        // 测量整个内容的高度
        const fullContentHeight = tempContent.offsetHeight
        const pageHeightInPixels = contentHeight * 3.78 // 转换mm为像素
        const totalPages = Math.ceil(fullContentHeight / pageHeightInPixels)

        // 设置html2canvas配置，优化渲染效果
        const opts = {
          scale: 3, // 高清晰度
          useCORS: true, // 跨域图片支持
          backgroundColor: '#ffffff', // 白色背景
          allowTaint: true, // 允许跨域
          logging: false, // 禁用日志
          removeContainer: false,
          imageTimeout: 60000, // 图片加载超时时间延长到60秒
          windowWidth: tempParent.offsetWidth,
          windowHeight: tempParent.offsetHeight,
          scrollX: 0,
          scrollY: 0,
          x: 0
        };

        // 计算更智能的分页偏移，尽量避免切割段落/表格/签名区
        const pageOffsets = this.computeSmartPageOffsets(tempContent, pageHeightInPixels)

        // 为每一页生成内容并添加到PDF
        for (let pageNum = 0; pageNum < pageOffsets.length; pageNum++) {
          // 添加新页面（除了第一页）
          if (pageNum > 0) {
            pdf.addPage()
          }

          // 计算当前页的内容位置
          const pageOffset = pageOffsets[pageNum]

          // 创建临时页面容器
          const pageContainer = document.createElement('div')
          pageContainer.style.position = 'absolute'
          pageContainer.style.top = '-9999px'
          pageContainer.style.left = '-9999px'
          pageContainer.style.width = `${contentWidth * 3.78}px`
          pageContainer.style.height = `${pageHeightInPixels}px`
          pageContainer.style.overflow = 'hidden'
          document.body.appendChild(pageContainer)

          // 克隆内容到页面容器
          const pageContent = tempContent.cloneNode(true)
          pageContent.style.marginTop = `-${pageOffset}px`
          pageContainer.appendChild(pageContent)

          // 为当前页生成canvas，使用精确的视口设置
          const pageCanvas = await html2Canvas(pageContainer, {
            ...opts,
            height: pageHeightInPixels,
            y: 0,
            scrollY: 0
          })

          // 生成图片数据
          const imgData = pageCanvas.toDataURL('image/jpeg', 1.0)

          // 计算图片在PDF中的尺寸
          const imgWidth = contentWidth
          const imgHeight = pageCanvas.height * imgWidth / pageCanvas.width

          // 在当前页添加图片，确保完整显示内容并居中对齐
          pdf.addImage(
            imgData,
            'JPEG',
            margin,  // 统一左页边距
            margin,  // 上页边距
            imgWidth,
            imgHeight
          )

          // 清理临时页面容器
          document.body.removeChild(pageContainer)
        }

        // 保存PDF文件
        pdf.save(`合同_${this.declarationId}.pdf`)

        // 清理主临时容器
        document.body.removeChild(tempParent)

        if (this.debugMode) {
          this.debugInfo += 'PDF生成成功！\n';
        }

        this.$message.success('PDF生成成功')
      } catch (pdfError) {
        console.error('PDF生成失败:', pdfError)

        if (this.debugMode) {
          this.debugInfo += `PDF生成失败: ${pdfError.message}\n`;
        }

        this.$message.error('PDF 导出失败：' + pdfError.message)
      } finally {
        this.pdfLoading = false
      }
    },

    // 为签名区域添加防分页样式
    addSignatureNoPageBreak(element) {
      // 查找所有包含签名相关关键词的元素
      const allElements = element.querySelectorAll('*')
      const signatureKeywords = ['签名', '签章', '签字', '盖章', '甲方法定代表人', '乙方法定代表人', '丙方法定代表人']
      
      allElements.forEach(el => {
        const text = el.textContent
        const containsKeyword = signatureKeywords.some(keyword => text.includes(keyword))
        
        if (containsKeyword) {
          // 为签名区域添加防分页样式
          el.style.pageBreakInside = 'avoid'
          el.style.pageBreakBefore = 'avoid'
          el.style.pageBreakAfter = 'avoid'
          
          // 找到包含签名区域的容器元素，向上查找最多3级，确保整个签名区域不被分割
          let container = el
          for (let i = 0; i < 3; i++) {
            if (container.parentElement && container.parentElement !== element) {
              container = container.parentElement
              container.style.pageBreakInside = 'avoid'
              container.style.pageBreakBefore = 'avoid'
              container.style.pageBreakAfter = 'avoid'
            } else {
              break
            }
          }
        }
      })
    },

    // 计算智能分页偏移，尽量在块级元素边界分页，避免切割内容
    computeSmartPageOffsets(rootElement, pageHeight) {
      try {
        const doc = rootElement.querySelector('.doc') || rootElement
        const children = Array.from(doc.children)
        if (children.length === 0) {
          return [0]
        }

        // 强制计算总高度以确保准确的页面计数
        const totalHeight = doc.offsetHeight
        const rawPageCount = Math.max(1, Math.ceil(totalHeight / pageHeight))

        // 找到所有签名区域元素
        const signatureElements = []
        const allElements = doc.querySelectorAll('*')
        allElements.forEach(el => {
          // 查找包含'签名'、'签章'、'签字'、'盖章'等关键词的元素
          const text = el.textContent.toLowerCase()
          if (text.includes('签名') || text.includes('签章') || text.includes('签字') || text.includes('盖章')) {
            signatureElements.push(el)
          }
          // 或者查找特定的类名或ID
          if (el.className.includes('signature') || el.id.includes('signature')) {
            signatureElements.push(el)
          }
        })

        const offsets = []
        let target = 0
        for (let i = 0; i < rawPageCount; i++) {
          // 理想页底位置
          target = i * pageHeight

          // 找到不超过目标位置的最近块边界
          let best = 0
          for (let j = 0; j < children.length; j++) {
            const el = children[j]
            const rectTop = el.offsetTop
            const rectBottom = rectTop + el.offsetHeight

            // 检查当前元素是否包含签名区域，或者签名区域是否跨越当前页边界
            let containsSignature = false
            signatureElements.forEach(sigEl => {
              // 找到签名元素的顶级父元素
              let parent = sigEl
              while (parent && parent !== doc) {
                if (parent === el) {
                  containsSignature = true
                  break
                }
                parent = parent.parentElement
              }
              
              // 检查签名区域是否跨越当前页边界
              if (!containsSignature) {
                const sigRect = sigEl.getBoundingClientRect()
                const docRect = doc.getBoundingClientRect()
                const sigTop = sigRect.top - docRect.top
                const sigBottom = sigTop + sigRect.height
                
                // 如果签名区域的顶部在当前页，底部在下一页，则需要调整分页位置
                if (sigTop >= target - pageHeight && sigTop < target && sigBottom > target) {
                  // 将分页位置调整到签名区域的顶部之前
                  best = Math.max(best, Math.min(target, sigTop - 20))
                }
              }
            })

            // 块元素底部不超过目标，作为候选
            if (rectBottom <= target) {
              // 避免分页在表格/签名区域/标题内部
              best = Math.max(best, rectBottom)
            }
          }

          // 如果没有找到合适的边界，使用上一个安全边界或0
          offsets.push(best || target)
        }

        // 确保最后一页从最后一个偏移开始，且唯一递增
        const uniqueOffsets = []
        let last = -1
        for (let k = 0; k < offsets.length; k++) {
          const v = Math.min(offsets[k], totalHeight)
          if (v > last) {
            uniqueOffsets.push(v)
            last = v
          }
        }

        // 第一页总是0
        if (uniqueOffsets.length === 0 || uniqueOffsets[0] !== 0) {
          uniqueOffsets.unshift(0)
        }

        return uniqueOffsets
      } catch (e) {
        console.error('智能分页计算失败:', e)
        // 回退到固定分页
        const doc = rootElement.querySelector('.doc') || rootElement
        const totalHeight = doc.offsetHeight
        const pageCount = Math.max(1, Math.ceil(totalHeight / pageHeight))
        return new Array(pageCount).fill(0).map((_, i) => i * pageHeight)
      }
    },

    // 转换图片为DataURL，解决跨域问题和确保图片加载
    convertImagesToDataURL(element) {
      const images = element.querySelectorAll('img');
      const promises = [];

      if (this.debugMode) {
        this.debugInfo += `开始处理 ${images.length} 张图片...\n`;
      }

      images.forEach((img, index) => {
        if (img.src) {
          const promise = new Promise((resolve) => {
            // 如果已经是DataURL，直接返回
            if (img.src.startsWith('data:')) {
              if (this.debugMode) {
                this.debugInfo += `图片 ${index + 1}: 已经是DataURL格式\n`;
              }
              resolve();
              return;
            }

            // 创建新的图片对象用于加载
            const newImg = new Image();
            newImg.crossOrigin = 'anonymous'; // 设置跨域属性

            // 保存原始src
            const originalSrc = img.src;

            if (this.debugMode) {
              this.debugInfo += `图片 ${index + 1}: 开始转换 ${originalSrc}\n`;
            }

            // 图片加载完成后绘制到canvas并转换为DataURL
            newImg.onload = function () {
              try {
                // 创建canvas用于转换
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // 设置canvas尺寸
                canvas.width = newImg.naturalWidth || newImg.width;
                canvas.height = newImg.naturalHeight || newImg.height;

                // 绘制图片到canvas
                ctx.drawImage(newImg, 0, 0);

                // 转换为DataURL
                const dataURL = canvas.toDataURL('image/png', 0.95);

                // 替换原图片的src为DataURL
                img.src = dataURL;

                if (this.debugMode) {
                  this.debugInfo += `图片 ${index + 1}: 转换成功\n`;
                }

                console.log(`图片转换成功: ${originalSrc} -> DataURL`);
                resolve();
              } catch (e) {
                console.warn('图片转换失败，保留原始图片:', e);
                if (this.debugMode) {
                  this.debugInfo += `图片 ${index + 1}: 转换失败，保留原始图片\n`;
                }
                // 如果转换失败，保留原始图片
                resolve();
              }
            }.bind(this);

            // 图片加载失败时的处理
            newImg.onerror = function () {
              console.warn('图片加载失败，尝试其他方法:', originalSrc);

              if (this.debugMode) {
                this.debugInfo += `图片 ${index + 1}: 加载失败，尝试重试\n`;
              }

              // 尝试添加时间戳重新加载
              try {
                const timestamp = new Date().getTime();
                const newSrc = originalSrc.includes('?') ? `${originalSrc}&t=${timestamp}` : `${originalSrc}?t=${timestamp}`;
                newImg.src = newSrc;
              } catch (e) {
                console.warn('重试加载失败，保留原始图片:', originalSrc);
                if (this.debugMode) {
                  this.debugInfo += `图片 ${index + 1}: 重试失败，保留原始图片\n`;
                }
                resolve();
              }
            }.bind(this);

            // 设置图片源
            newImg.src = originalSrc;

            // 添加超时处理
            setTimeout(() => {
              if (!newImg.complete) {
                console.warn('图片加载超时，保留原始图片:', originalSrc);
                if (this.debugMode) {
                  this.debugInfo += `图片 ${index + 1}: 加载超时，保留原始图片\n`;
                }
                resolve();
              }
            }, 20000);
          });

          promises.push(promise);
        }
      });

      // 等待所有图片处理完成
      return Promise.all(promises);
    },

    // 优化丙方公章显示效果
    optimizeSealDisplay(element) {
      // 查找丙方签名区域
      const sealImages = element.querySelectorAll('img');
      sealImages.forEach(img => {
        // 检查是否是丙方公章（通过父元素的文本内容判断）
        const parentText = img.parentElement ? img.parentElement.textContent : '';
        if (parentText.includes('丙方') && img.style.position === 'absolute') {
          // 优化公章显示效果
          img.style.opacity = '0.85';
          img.style.mixBlendMode = 'multiply';
          img.style.filter = 'contrast(1.3) brightness(0.85) saturate(1.2)';
          img.style.zIndex = '10';

          // 确保公章能正确覆盖在文字上
          const parentDiv = img.parentElement;
          if (parentDiv) {
            parentDiv.style.position = 'relative';
            parentDiv.style.overflow = 'visible';
          }
        }
      });
    },

    // 等待所有图片加载完成 - 增强版
    waitForImagesLoaded(element) {
      return new Promise((resolve) => {
        const images = element.querySelectorAll('img')
        if (images.length === 0) {
          resolve()
          return
        }

        let loadedCount = 0
        const totalImages = images.length

        // 为每个图片添加加载超时处理
        const timeoutIds = []

        const checkComplete = (img, src) => {
          loadedCount++
          // 移除超时计时器
          const index = timeoutIds.findIndex(item => item.src === src)
          if (index !== -1) {
            clearTimeout(timeoutIds[index].timeoutId)
            timeoutIds.splice(index, 1)
          }

          if (loadedCount === totalImages) {
            resolve()
          }
        }

        images.forEach(img => {
          const src = img.src

          // 为图片添加跨域支持
          img.crossOrigin = 'anonymous'

          // 记录图片加载状态
          console.log(`等待图片加载: ${src}, 已完成: ${img.complete}`)

          if (img.complete) {
            checkComplete(img, src)
          } else {
            // 添加事件监听器
            img.onload = function () {
              console.log(`图片加载成功: ${src}`)
              checkComplete(this, src)
            }
            img.onerror = function () {
              console.warn(`图片加载失败: ${src}`)
              checkComplete(this, src) // 即使图片加载失败也继续
            }

            // 添加超时处理
            const timeoutId = setTimeout(() => {
              console.warn(`图片加载超时: ${src}`)
              checkComplete(img, src) // 超时也继续
            }, 15000) // 15秒超时

            timeoutIds.push({ src, timeoutId })

            // 尝试重新加载图片以解决缓存问题
            if (src) {
              // 添加时间戳以避免缓存
              const timestamp = new Date().getTime()
              const newSrc = src.includes('?') ? `${src}&t=${timestamp}` : `${src}?t=${timestamp}`
              // 延迟重新设置src以确保事件监听器已添加
              setTimeout(() => {
                try {
                  img.src = newSrc
                } catch (e) {
                  console.warn(`重新加载图片失败: ${src}`, e)
                }
              }, 100)
            }
          }
        })

        // 添加全局超时，确保函数最终会解析
        setTimeout(() => {
          if (loadedCount < totalImages) {
            console.warn(`部分图片加载超时，已加载 ${loadedCount}/${totalImages} 张图片`)
            resolve() // 强制解析，继续执行
          }
        }, 30000) // 30秒全局超时
      })
    },
    onClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    toggleDebug() {
      this.debugMode = !this.debugMode;
      this.debugInfo = ''; // 切换时清空信息
      if (this.debugMode) {
        this.debugInfo = `
          调试信息已开启。
          当前合同HTML长度: ${this.contractHtml.length}
          当前PDF生成状态: ${this.pdfLoading ? '生成中' : '未生成'}
          当前图片加载状态: ${this.debugInfo.includes('图片加载') ? '部分加载' : '全部加载'}
        `;
      }
    }
  }
}
</script>

<style scoped>
/* 基本容器和工具样式 */
.toolbar {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-text {
  font-size: 12px;
  color: #666;
  margin-left: 10px;
}

.contract-wrapper {
  max-height: 70vh;
  overflow: auto;
  background: #f5f6f8;
  padding: 16px;
}

.contract-print {
  color: #222;
  font-size: 14px;
  line-height: 2;
  font-family: "SimSun", "宋体", "Songti SC", serif;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* 调试信息样式 */
.debug-info {
  margin-top: 10px;
  padding: 10px;
  background-color: #f5f7fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 12px;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-all;
  overflow-x: auto;
}

/* 响应式优化 */
@media (max-width: 768px) {
  .contract-wrapper {
    padding: 8px;
  }
}

/* 打印样式 */
@media print {
  .toolbar {
    display: none;
  }

  .contract-wrapper {
    max-height: initial;
    overflow: visible;
    background: #fff;
    padding: 0;
  }

  @page {
    size: A4;
    margin: 12mm;
    /* 打印机物理边距适配 */
    @top-center {
      content: "日丰地暖质保协议";
      font-size: 12px;
      color: #666;
    }
    @bottom-center {
      content: "第 " counter(page) " 页，共 " counter(pages) " 页";
      font-size: 12px;
      color: #666;
    }
  }
}
</style>